// src/components/UserRoleProvider.tsx

import React, { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { UserStore } from '@stores';
import apiResponse from '../../api/ApiResponse';
import { roleMap } from '@utils/RoleUtils';
import { AppCookieName, useAppCookies } from '@app/providers/AppCookieProvider';

export type UserRoleStatus = 'loading' | 'loggedOut' | 'error' | 'loaded';

interface UserRoleState {
  status: UserRoleStatus;
  role: number;
  retry: () => void;
}

// Create a context for the user's roles
const UserRoleContext = createContext<UserRoleState>({ status: 'loading', role: 0, retry: () => undefined });

interface UserRoleProviderProps {
  children: ReactNode;
}

const ROLE_LOADING = { status: 'loading', role: 0 } as const;
const ROLE_FAILED = { status: 'error', role: 0 } as const;

// Provider component to wrap the application or part of it
export const UserRoleProvider: React.FC<UserRoleProviderProps> = ({ children }) => {
  const cookies = useAppCookies();
  const [, , removeCookie] = useCookies<AppCookieName>([]);
  const [roleRequest, setRoleRequest] = useState<{ status: UserRoleStatus; role: number }>(ROLE_LOADING);
  const [attempt, setAttempt] = useState(0);

  const isLoggedIn = cookies.auth.available && cookies.profile.available;
  const email = cookies.profile.email;
  const status = isLoggedIn ? roleRequest.status : 'loggedOut';

  const retry = useCallback(() => setAttempt((current) => current + 1), []);

  useEffect(() => {
    setRoleRequest(ROLE_LOADING);
    if (!isLoggedIn) return;

    let isCurrent = true;

    (async () => {
      try {
        const response = await new UserStore().getUserRole(email);
        if (!isCurrent) return;

        if (response.statusCode === 401) {
          // Sign-in sends anyone with these cookies straight back here, so they have to go. The 401 has already
          // cleared them, including the httpOnly session; removing them here tells react-cookie straight away. If
          // they don't clear, the error state below stops the person bouncing between this page and sign-in.
          const cookieOptions = { path: '/', domain: window.location.hostname };
          removeCookie(AppCookieName.AUTH, cookieOptions);
          removeCookie(AppCookieName.PROFILE, cookieOptions);
          setRoleRequest(ROLE_FAILED);
          return;
        }

        const role = await apiResponse.handleApiResponse(async () => response);
        if (isCurrent) setRoleRequest({ status: 'loaded', role: roleMap(role.split('|')) });
      } catch {
        if (isCurrent) setRoleRequest(ROLE_FAILED);
      }
    })();

    return () => {
      isCurrent = false;
    };
  }, [isLoggedIn, email, attempt]);

  // ApiService has already retried by the time this is an error, so wait for a reason to think it may work now
  useEffect(() => {
    if (status !== 'error') return;

    const retryIfVisible = () => {
      if (document.visibilityState === 'visible') retry();
    };

    document.addEventListener('visibilitychange', retryIfVisible);
    window.addEventListener('online', retry);
    return () => {
      document.removeEventListener('visibilitychange', retryIfVisible);
      window.removeEventListener('online', retry);
    };
  }, [status, retry]);

  const value = useMemo(() => ({ status, role: roleRequest.role, retry }), [status, roleRequest.role, retry]);

  return <UserRoleContext.Provider value={value}>{children}</UserRoleContext.Provider>;
};

export { UserRoleContext };
